import { Loader2, AlertCircle } from "lucide-react";
import React, { useState, useEffect } from "react";
import PlacesAutocomplete, { Suggestion } from "react-places-autocomplete";

// Environment variable for production

interface DeliveryInfo {
    address: string;
    [key: string]: unknown;
}

interface Item {
    id: string | number;
    [key: string]: unknown;
}

interface AddressInputProps {
    item: Item;
    deliveryInfo: DeliveryInfo;
    handleAddressChange: (itemId: string | number, address: string) => void;
}

interface PlacesRenderProps {
    getInputProps: (options?: Record<string, unknown>) => Record<string, unknown>;
    suggestions: Suggestion[];
    getSuggestionItemProps: (
        suggestion: Suggestion,
        options?: Record<string, unknown>
    ) => Record<string, unknown>;
    loading: boolean;
}

type PlacesAutocompletePropsLike = {
    value: string;
    onChange: (value: string) => void;
    onSelect: (address: string, placeId?: string) => void;
    searchOptions?: Record<string, unknown>;
    children: (props: PlacesRenderProps) => React.ReactNode;
};

const PlacesAutocompleteComponent =
    PlacesAutocomplete as unknown as React.ComponentType<PlacesAutocompletePropsLike>;

const AddressInput: React.FC<AddressInputProps> = ({ item, deliveryInfo, handleAddressChange }) => {
    const [address, setAddress] = useState<string>(deliveryInfo.address || "");
    const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);
    const [scriptError, setScriptError] = useState<boolean>(false);

    // Load Google Maps script dynamically
    useEffect(() => {
        if (globalThis.google && globalThis.google.maps && globalThis.google.maps.places) {
            setScriptLoaded(true);
            return;
        }

        const script = document.createElement("script");
        const apiKey = process.env.REACT_APP_PLACES_API_KEY;

        if (!apiKey) {
            setScriptError(true);
            return;
        }

        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            setScriptLoaded(true);
        };

        script.onerror = () => {
            setScriptError(true);
        };

        document.head.appendChild(script);

        // No cleanup needed; script can persist across component mounts
    }, []);

    // Sync address with deliveryInfo.address
    useEffect(() => {
        setAddress(deliveryInfo.address || "");
    }, [deliveryInfo.address]);

    // Handle place selection and fetch details
    const handleSelect = (selectedAddress: string, placeId?: string) => {
        setAddress(selectedAddress);

        if (!placeId) {
            handleAddressChange(item.id, selectedAddress);
            return;
        }

        const placesService = new globalThis.google.maps.places.PlacesService(
            document.createElement("div")
        );

        const request: google.maps.places.PlaceDetailsRequest = {
            placeId: placeId,
            fields: ["formatted_address", "name"],
        };

        placesService.getDetails(request, (place, status) => {
            if (status === globalThis.google.maps.places.PlacesServiceStatus.OK && place) {
                const formattedAddress = place.formatted_address || selectedAddress;
                handleAddressChange(item.id, formattedAddress);
            } else {
                handleAddressChange(item.id, selectedAddress);
            }
        });
    };

    // Handle manual input
    const handleChange = (value: string) => {
        setAddress(value);
        handleAddressChange(item.id, value);
    };

    // Render loading or error states
    if (scriptError) {
        return (
            <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="h-5 w-5" />
                <span>Failed to load address input. Please try again later.</span>
            </div>
        );
    }

    if (!scriptLoaded) {
        return (
            <div className="flex items-center gap-2 text-gray-600">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className=" text-sm">Loading address input...</span>
            </div>
        );
    }

    return (
        <PlacesAutocompleteComponent
            value={address}
            onChange={handleChange}
            onSelect={handleSelect}
            searchOptions={{
                types: ["address"],
                componentRestrictions: { country: "gb" }, // Restrict to UK
            }}
        >
            {({
                getInputProps,
                suggestions,
                getSuggestionItemProps,
                loading,
            }: PlacesRenderProps) => (
                <div className="relative">
                    {/* Input Field */}
                    {(() => {
                        const inputProps = getInputProps({
                            placeholder: "Enter Address",
                            className:
                                "w-full px-4 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-[1px] focus:border-[#317333] focus:border-[#317333] transition-all duration-200",
                        }) as React.InputHTMLAttributes<HTMLInputElement>;
                        return <input {...inputProps} />;
                    })()}

                    {/* Autocomplete Dropdown */}
                    {suggestions.length > 0 || loading ? (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center gap-2 p-3 text-gray-600">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span className=" text-sm">Loading suggestions...</span>
                                </div>
                            ) : (
                                suggestions.map((suggestion: Suggestion) => (
                                    <div
                                        {...getSuggestionItemProps(suggestion, {
                                            className: `p-3 text-sm cursor-pointer transition-colors duration-150 ${suggestion.active
                                                ? "bg-gray-100 text-gray-900"
                                                : "bg-white text-gray-700"
                                                } hover:bg-gray-100`,
                                        })}
                                        key={suggestion.placeId}
                                    >
                                        {suggestion.description}
                                    </div>
                                ))
                            )}
                        </div>
                    ) : null}
                </div>
            )}
        </PlacesAutocompleteComponent>
    );
};

export default AddressInput;
